import React, { useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { ImageSource, isVideoUrl } from 'ts-core';
import { Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
// Swiper ships positioning CSS as separate side-effect imports. The map embed
// lib build entries (`index.webcomponent.ts` etc.) don't pull these in, so
// without colocating them here the prev/next arrows render as unstyled
// fallback DOM at the bottom of the lightbox flow — they show up "over there
// by the events list" because their `position: absolute` rule never lands.
import 'swiper/css';
import 'swiper/css/navigation';

interface LightboxProps {
    /** All images/videos to display in the lightbox slider. */
    images: ImageSource[];
    /** Index of the initially visible slide. */
    initialIndex: number;
    /** Called when the lightbox should close. */
    onClose: () => void;
}

/**
 * Scoped styles for the lightbox Swiper navigation arrows.
 * Uses a unique wrapper class to avoid leaking into the page carousel.
 */
const LIGHTBOX_NAV_STYLES = `
.be-lightbox .swiper-button-prev,
.be-lightbox .swiper-button-next {
  color: white;
}
.be-lightbox .swiper-button-prev::after,
.be-lightbox .swiper-button-next::after {
  font-size: 1.5rem;
  font-weight: bold;
}
`;

/**
 * Simple lightbox modal for viewing event images and videos.
 *
 * - Full-viewport overlay with black background at 80% opacity
 * - Swiper slider with navigation arrows (when >1 image)
 * - Close button (top-right) and Escape key to dismiss
 * - Body scroll is locked while open
 */
export function Lightbox({ images, initialIndex, onClose }: LightboxProps) {
    const handleKeyDown = useCallback(
        (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        },
        [onClose],
    );

    // Lock body scroll and listen for Escape
    useEffect(() => {
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.body.style.overflow = prev;
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [handleKeyDown]);

    const hasMultiple = images.length > 1;

    return createPortal(
        // `.be-embedded-app` wrapper keeps the map embed's scoped Tailwind
        // CSS applicable to portal content rendered at document.body. Outside
        // the embed (no map.css loaded) the class is inert.
        <div className="be-embedded-app">
            <style>{LIGHTBOX_NAV_STYLES}</style>
            {/* Backdrop */}
            <div
                className="be-lightbox fixed inset-0 z-[9999] flex items-center justify-center"
                role="dialog"
                aria-modal="true"
                aria-label="Image lightbox"
            >
                {/* Black overlay */}
                <div
                    className="absolute inset-0 bg-black/80"
                    onClick={onClose}
                />

                {/* Close button */}
                <button
                    type="button"
                    className="absolute right-4 top-4 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-black/50 text-2xl text-white transition-colors hover:bg-black/70"
                    onClick={onClose}
                    aria-label="Close lightbox"
                >
                    ✕
                </button>

                {/* Slider */}
                <div className="relative z-[1] h-full w-full">
                    <Swiper
                        className="h-full w-full"
                        initialSlide={initialIndex}
                        slidesPerView={1}
                        navigation={hasMultiple}
                        modules={[Navigation]}
                    >
                        {images.map((img, i) => (
                            <SwiperSlide
                                key={i}
                                className="flex items-center justify-center"
                            >
                                <div className="flex h-full w-full items-center justify-center p-12">
                                    {isVideoUrl(img.url) ? (
                                        <video
                                            src={img.url}
                                            className="max-h-full max-w-full object-contain"
                                            controls
                                            autoPlay
                                            muted
                                            playsInline
                                            onClick={(e) => e.stopPropagation()}
                                        />
                                    ) : (
                                        <img
                                            src={img.url}
                                            alt="Full size"
                                            className="max-h-full max-w-full object-contain"
                                        />
                                    )}
                                    {img.copyright && img.copyright !== 'null' && (
                                        <p className="absolute bottom-6 right-6 z-10 rounded-md bg-black/50 px-2 py-1 text-xs text-white">
                                            ©{img.copyright}
                                        </p>
                                    )}
                                </div>
                            </SwiperSlide>
                        ))}
                    </Swiper>
                </div>
            </div>
        </div>,
        document.body,
    );
}
